import React from "react";
import { Container } from "react-bootstrap";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "../shared/Breadcrumbs";
import { useGetNavigationListByUser } from "@/services/hooks/navigationHooks";
import { usePathname } from "next/navigation";
import { MenuRouteItem } from "../menu/types";
import { usePreference } from "@/context/preference-context";
import { useTheme } from "next-themes";
import { useLayoutOverrides } from "@/components/layout/LayoutOverridesContext";
import { useRecoverableProtectedAuth } from "@/hooks/useRecoverableProtectedAuth";

interface MainLayoutProps {
    children: React.ReactNode;
    showHeader?: boolean;
    showFooter?: boolean;
    fluid?: boolean;
}

const renderBreadcrumb = (
    isAuthenticated: boolean,
    navigationMenus: MenuRouteItem[],
    pathname: string,
    isFluid: boolean,
    userId?: number,
    isExternalUser?: boolean,
    showBreadcrumbs?: boolean
) => {
    // 🔥 FIX: Exclude external dashboard routes from breadcrumb rendering
    const excludedPaths = [
        "/",
        "/dashboard",
        "/externalLoginDashboard", // 🔥 NEW: Hide breadcrumbs for external dashboard
    ];

    // Also exclude any sub-routes of external dashboard
    const isExternalDashboardRoute = pathname.startsWith(
        "/externalLoginDashboard"
    );

    const shouldShowBreadcrumbs =
        isAuthenticated &&
        navigationMenus &&
        !excludedPaths.includes(pathname) &&
        !isExternalDashboardRoute &&
        showBreadcrumbs !== false; // 🔥 NEW: Allow layout overrides

    return (
        shouldShowBreadcrumbs && (
            <div
                className={`main-layout-breadcrumbs d-flex justify-content-start mt-3 ${isFluid ? "px-4" : "px-3"}`}
            >
                <Breadcrumbs
                    menuData={navigationMenus}
                    userId={userId}
                    isExternalUser={isExternalUser}
                />{" "}
                {/* <-- pass it */}
            </div>
        )
    );
};

const MainLayout: React.FC<MainLayoutProps> = ({
    children,
    showHeader = true,
    showFooter = true,
    fluid = false,
}) => {
    const { showHeader: overrideShowHeader, showBreadcrumbs } =
        useLayoutOverrides();
    const {
        isAuthenticated: stableIsAuthenticated,
        currentUser: stableCurrentUser,
        loginType: stableLoginType,
    } = useRecoverableProtectedAuth();
    const pathname = usePathname() ?? "";

    // Only fetch navigation if we have a valid authenticated user with userId
    const { data: navigationMenus } = useGetNavigationListByUser(
        stableCurrentUser?.userId || 0,
        {
            enabled:
                stableIsAuthenticated &&
                !!stableCurrentUser?.userId &&
                stableCurrentUser.userId > 0,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any
    );

    return (
        <div
            className={`theme-container d-flex flex-column min-vh-100 ${fluid ? "layout-fluid" : "layout-fixed"}`}
        >
            {showHeader && overrideShowHeader !== false && <Header />}
            <ThemePreferenceSyncer />
            {fluid ? (
                <div className="flex-grow-1">
                    {navigationMenus &&
                        renderBreadcrumb(
                            stableIsAuthenticated,
                            navigationMenus,
                            pathname || "",
                            fluid,
                            stableCurrentUser?.userId, // <-- pass it
                            stableLoginType === "external",
                            showBreadcrumbs
                        )}
                    {children}
                </div>
            ) : (
                <Container className="flex-grow-1">
                    <>
                        {navigationMenus &&
                            renderBreadcrumb(
                                stableIsAuthenticated,
                                navigationMenus,
                                pathname || "",
                                fluid,
                                stableCurrentUser?.userId,
                                stableLoginType === "external",
                                showBreadcrumbs
                            )}
                        {children}
                    </>
                </Container>
            )}
            {showFooter && <Footer />}
        </div>
    );
};

export default MainLayout;

const ThemePreferenceSyncer = () => {
    const { preference } = usePreference();
    const { theme, setTheme } = useTheme();
    const lastAppliedPreferenceThemeRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        const preferredTheme = preference?.theme;

        if (preferredTheme !== "light" && preferredTheme !== "dark") {
            return;
        }

        if (preferredTheme === lastAppliedPreferenceThemeRef.current) {
            return;
        }

        lastAppliedPreferenceThemeRef.current = preferredTheme;

        if (theme !== preferredTheme) {
            setTheme(preferredTheme);
        }
    }, [preference?.theme, setTheme, theme]);

    return null;
};
